/**
 * Budget Consolidation — org selection, and how it is encoded in the URL.
 *
 * The tree cascades downwards only (checking a node checks its subtree; unchecking one branch leaves
 * the ancestor checked), so a selection is not simply "a set of subtrees". It is fully described by the
 * pair the rollup API already takes:
 *
 *   roots   — the tops of the checked branches (a checked node whose parent is not checked)
 *   exclude — the tops of the unchecked branches under a still-checked parent
 *
 * That pair is what goes in the URL. Storing the expanded checked set instead makes "select the whole
 * group" O(tree): 39 characters per node, so a few hundred nodes push a bookmarked link past the ~8 KB
 * request-line limit of every static host we run behind, and the reload fails with 414 before the app
 * boots. The pair is O(1) for that case.
 *
 * `expandSelection` is the exact inverse of `topMostChecked` + `excludedBranchTops` — see the round-trip
 * test. It rebuilds the checked set by walking the forest top-down rather than by subtracting excluded
 * subtrees from selected ones, which would be wrong whenever a node is re-checked *below* an unchecked
 * branch (root checked → child unchecked → grandchild re-checked; subtracting the child's subtree would
 * drop the grandchild the user can see is checked).
 */

export interface OrgTreeIndex {
  /** Every known node id, in the order the source list supplied them. */
  allNodeIds: readonly string[];
  /** id → parent id. Present for every node; the value is undefined/null for a forest root. */
  parentById: ReadonlyMap<string, string | null | undefined>;
  /** id → child ids. Absent for a leaf. */
  childrenById: ReadonlyMap<string, readonly string[]>;
}

export interface SelectionParams {
  roots: string[];
  exclude: string[];
}

/** The initial selection read off the URL: either ready to use, or pending the org tree. */
export interface InitialSelection {
  /** Usable immediately — a route param or a legacy literal `nodes=` list. */
  selected: string[];
  /** Needs the org tree before it can be expanded; null when there is nothing to hydrate. */
  pending: SelectionParams | null;
}

export function buildOrgTreeIndex(
  orgUnits: readonly { id: string; parentId?: string | null }[],
): OrgTreeIndex {
  const allNodeIds: string[] = [];
  const parentById = new Map<string, string | null | undefined>();
  const childrenById = new Map<string, string[]>();

  for (const o of orgUnits) {
    allNodeIds.push(o.id);
    parentById.set(o.id, o.parentId);
  }
  // A parent outside the loaded set is treated as absent, matching how the tree builder roots such a
  // node — otherwise its subtree would be unreachable from any forest root and silently vanish.
  for (const o of orgUnits) {
    if (o.parentId && parentById.has(o.parentId)) {
      childrenById.set(o.parentId, [...(childrenById.get(o.parentId) ?? []), o.id]);
    }
  }
  return { allNodeIds, parentById, childrenById };
}

/** Every id in the subtree rooted at `rootId`, including the root itself. Cycle-guarded. */
export function collectSubtree(rootId: string, index: OrgTreeIndex): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  const stack = [rootId];
  while (stack.length) {
    const id = stack.pop()!;
    if (seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
    stack.push(...(index.childrenById.get(id) ?? []));
  }
  return ids;
}

/** The subtree roots of a selection: a checked node whose parent is not checked. */
export function topMostChecked(selectedIds: readonly string[], index: OrgTreeIndex): string[] {
  const selected = new Set(selectedIds);
  return selectedIds.filter((id) => {
    const parent = index.parentById.get(id);
    return !parent || !selected.has(parent);
  });
}

/** The tops of the pruned branches: an unchecked node whose parent is checked. */
export function excludedBranchTops(selectedIds: readonly string[], index: OrgTreeIndex): string[] {
  const selected = new Set(selectedIds);
  return index.allNodeIds.filter((id) => {
    const parent = index.parentById.get(id);
    return !selected.has(id) && !!parent && selected.has(parent);
  });
}

/**
 * Rebuild the checked set from `roots` + `exclude` — the exact inverse of the two functions above.
 *
 * Walks the forest top-down carrying the inherited checked state: a node in `roots` is checked, a node
 * in `exclude` is unchecked, anything else inherits from its parent. An id in `roots` that is not in the
 * tree is dropped rather than raising, so a stale bookmark degrades instead of breaking.
 */
export function expandSelection(
  roots: readonly string[],
  exclude: readonly string[],
  index: OrgTreeIndex,
): string[] {
  const rootSet = new Set(roots);
  const excludeSet = new Set(exclude);
  const selected: string[] = [];
  const seen = new Set<string>();

  const stack = index.allNodeIds
    .filter((id) => {
      const parent = index.parentById.get(id);
      return !parent || !index.parentById.has(parent);
    })
    .map((id) => ({ id, inherited: false }));

  while (stack.length) {
    const { id, inherited } = stack.pop()!;
    if (seen.has(id)) continue;
    seen.add(id);
    // An explicit root wins over an explicit exclusion; a valid encoding never contains both, and
    // preferring the selection is the safer reading of a hand-edited URL.
    const checked = rootSet.has(id) ? true : excludeSet.has(id) ? false : inherited;
    if (checked) selected.push(id);
    for (const child of index.childrenById.get(id) ?? []) {
      stack.push({ id: child, inherited: checked });
    }
  }
  return selected;
}

/**
 * The current legal entity's subtree, or nothing when no legal entity is selected.
 *
 * Budget Consolidation is single-LE: only one legal entity's nodes are offered for selection. The
 * rollup service itself still spans legal entities by design — it disables the scope filter and walks
 * the org tree — so this is a deliberate narrowing of the *selection*, reversible by dropping the call.
 *
 * The reason is coherence rather than permissions: a plan version is LE-scoped, so a selection covering
 * two legal entities is still consolidated against one of them and the other branch reads as a silent
 * zero. Until a version (and a statement template) means something across legal entities, offering the
 * selection is offering a wrong answer.
 */
export function scopeToLegalEntity<T extends { id: string; parentId?: string | null }>(
  orgUnits: readonly T[],
  legalEntityId: string | null | undefined,
): T[] {
  if (!legalEntityId) return [];
  const subtree = new Set(collectSubtree(legalEntityId, buildOrgTreeIndex(orgUnits)));
  return orgUnits.filter((o) => subtree.has(o.id));
}

/** The caller's consolidation scope, as served by `GET /api/finance/planbudgetrollup/org-scope`. */
export interface OrgScope {
  /** No org bound (the caller holds `Finance.PlanEntry.ViewRollupAll`); every node is selectable. */
  unrestricted: boolean;
  /** The caller's active membership nodes — each the root of a subtree they may consolidate. */
  rootOrgUnitIds: string[];
}

/** The org tree split into what may be selected and what is only shown for orientation. */
export interface ScopedOrgTree<T> {
  /** The nodes to render: the selectable subtrees plus the context ancestors. */
  nodes: T[];
  /**
   * Ancestors above the caller's membership nodes. Rendered so the reader can see where their part
   * of the organisation sits (and so several memberships hang off a common stem) but never
   * selectable — consolidating them is exactly what the server denies.
   */
  contextIds: Set<string>;
}

/**
 * Narrow the org tree to what the caller may consolidate, keeping their ancestors as context.
 *
 * The server-side gate in the rollup reads is the boundary; this is its mirror, so the picker cannot
 * offer a node the request would 403 on. Showing the ancestors discloses nothing new — these users
 * hold `Core.OrgUnit.Read` and see the whole tree on the Organization page — and they carry no
 * figures: every consolidated number still comes from a scoped read.
 *
 * A membership node that is not in `orgUnits` (outside the current legal entity, or inactive)
 * contributes nothing, which is what keeps this composable with `scopeToLegalEntity`.
 */
export function applyOrgScope<T extends { id: string; parentId?: string | null }>(
  orgUnits: readonly T[],
  scope: OrgScope,
): ScopedOrgTree<T> {
  if (scope.unrestricted) {
    return { nodes: [...orgUnits], contextIds: new Set() };
  }

  const index = buildOrgTreeIndex(orgUnits);
  const known = new Set(index.allNodeIds);

  const selectable = new Set<string>();
  for (const rootId of scope.rootOrgUnitIds) {
    if (!known.has(rootId)) continue;
    for (const id of collectSubtree(rootId, index)) selectable.add(id);
  }

  // Walk up from each membership node. The walk stops at a parent outside the loaded set — the same
  // rule buildOrgTreeIndex applies to children, and what keeps this composable with
  // scopeToLegalEntity: a node that is not rendered must not end up flagged as context. Cycle-guarded
  // per walk; a node that is itself selectable (member of both a parent and a child) stays
  // selectable — context never demotes it.
  const contextIds = new Set<string>();
  for (const rootId of scope.rootOrgUnitIds) {
    if (!known.has(rootId)) continue;
    const seen = new Set<string>([rootId]);
    let current = index.parentById.get(rootId);
    while (current && known.has(current) && !seen.has(current)) {
      seen.add(current);
      if (!selectable.has(current)) contextIds.add(current);
      current = index.parentById.get(current);
    }
  }

  return {
    nodes: orgUnits.filter((o) => selectable.has(o.id) || contextIds.has(o.id)),
    contextIds,
  };
}

/**
 * The checked set the tree should render, given the selection the request is built from.
 *
 * A context ancestor is never part of the stored selection — the server refuses to root a
 * consolidation at a node the caller is not under — but leaving its checkbox blank while everything
 * beneath it is ticked reads as a bug. It is therefore shown checked exactly when every selectable
 * node in its subtree is checked, which also makes ticking it mean "select all of my part below
 * here". Derived rather than stored, so a reload of the roots/exclude URL reproduces it.
 */
export function withContextChecked(
  selectedIds: readonly string[],
  contextIds: ReadonlySet<string>,
  selectableIds: ReadonlySet<string>,
  index: OrgTreeIndex,
): string[] {
  const checked = new Set(selectedIds);
  for (const contextId of contextIds) {
    const beneath = collectSubtree(contextId, index).filter((id) => selectableIds.has(id));
    if (beneath.length > 0 && beneath.every((id) => checked.has(id))) {
      checked.add(contextId);
    }
  }
  return [...checked];
}

/** The selection as query params. `exclude` is omitted when empty so the common URL stays short. */
export function toSelectionParams(selectedIds: readonly string[], index: OrgTreeIndex): SelectionParams {
  return {
    roots: topMostChecked(selectedIds, index),
    exclude: excludedBranchTops(selectedIds, index),
  };
}

/**
 * Read the initial selection from the route param and query string.
 *
 * `roots`/`exclude` is the current encoding and needs the org tree to expand. `nodes` is the legacy
 * encoding — a literal checked set — and is honoured verbatim: expanding it with the current rules would
 * silently widen any selection that had a branch unchecked, since the legacy form recorded no exclusions.
 */
export function parseInitialSelection(
  routeOrgUnitId: string | undefined,
  query: URLSearchParams,
): InitialSelection {
  if (routeOrgUnitId) return { selected: [routeOrgUnitId], pending: null };

  const roots = splitIds(query.get('roots'));
  if (roots.length) {
    return { selected: [], pending: { roots, exclude: splitIds(query.get('exclude')) } };
  }

  return { selected: splitIds(query.get('nodes')), pending: null };
}

function splitIds(raw: string | null): string[] {
  return raw ? raw.split(',').filter(Boolean) : [];
}
